import { useUser } from "@/composables/user/user";
import { useUserSettings } from "@/composables/user/userSettings";
import { TokenService } from "@/services/user/auth/login/tokenService";
import { AccountType } from "@/services/user/userTypes";
import { addTokenToUrl, catch401 } from "@/services/apiClient/helpers";
import { ApiClient } from "@/services/apiClient/ApiClient";
import { createHomepageLink } from "@/router/linkFactories";
import { routerRef, whenRouterReady } from "@/router/routerRef";
import { logout as googleLogout } from "@/services/user/auth/login/sso/GoogleLoginService";

export const afterLogout = async (invalidateInApi: boolean): Promise<void> => {
  const { user } = useUser();
  if (!user.value) {
    return;
  }

  await ssoLogout(user.value.type);

  if (invalidateInApi) {
    await invalidateSessionInApi();
  }

  clearUserState();
  // Lazy import breaks a circular dependency (this file is reachable from
  // axiosFactory.ts's 401-handling `logout`, which useCartStatus's chain loops back to via ApiClient).
  const { useCartStatus } = await import("@/composables/marketplace/cart/useCartStatus");
  useCartStatus().refresh();
  redirectAfterLogout();
};

const ssoLogout = async (accountType: AccountType): Promise<void> => {
  if (accountType === AccountType.Facebook) {
    // Lazy import breaks a circular dependency: FacebookLoginService statically imports
    // router.ts, which calls registerGuards(), which accesses startupUserCheck before
    // its module finishes evaluating (TDZ) when reached via the auth chain.
    const { Facebook } = await import("@/services/user/auth/login/sso/FacebookLoginService");
    Facebook.logout();
  } else if (accountType === AccountType.Google) {
    googleLogout();
  }
};

const invalidateSessionInApi = async (): Promise<void> => {
  const url = addTokenToUrl("profile/logout");
  await ApiClient.put(url).catch(catch401);
};

const clearUserState = (): void => {
  const { user } = useUser();
  const { userSettings, bankInfo, isEmailVerified } = useUserSettings();

  TokenService.delete();
  user.value = undefined;
  userSettings.value = undefined;
  bankInfo.value = undefined;
  isEmailVerified.value = undefined;
};

const redirectAfterLogout = (): void => {
  // Unset routerRef ⇒ a stored token was rejected during the initial navigation, before App.vue
  // mounted. Chained rather than awaited: the router only appears once that navigation finishes.
  if (routerRef !== undefined) {
    routerRef.push(createHomepageLink());
  } else {
    void whenRouterReady().then((router) => router.replace(createHomepageLink()));
  }
};
